import { useEffect, useMemo, useState } from 'react';
import { arcadeAudio } from '../../platform/audio';
import { loadJson, saveJson } from '../../platform/storage';
import type { GameProps } from '../../platform/types';
import './felt.css';

type Suit = 'C' | 'D' | 'H' | 'P';
interface Card { r: number; s: Suit; id: string }
type Table = 'lobby' | 'blackjack' | 'holdem' | 'klondike' | 'pyramid' | 'highlow';

const SUITS: Suit[] = ['C', 'D', 'H', 'P'];
const RANKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

function deck(): Card[] {
  const d: Card[] = [];
  for (const s of SUITS) for (const r of RANKS) d.push({ r, s, id: `${r}${s}${Math.random().toString(36).slice(2, 6)}` });
  for (let i = d.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}
function face(c: Card) {
  const n = c.r === 1 ? 'A' : c.r === 11 ? 'J' : c.r === 12 ? 'Q' : c.r === 13 ? 'K' : String(c.r);
  return `/assets/cards/${n}-${c.s}.png`;
}
function valBJ(hand: Card[]) {
  let t = 0; let a = 0;
  for (const c of hand) {
    if (c.r === 1) { a += 1; t += 11; }
    else t += Math.min(10, c.r);
  }
  while (t > 21 && a) { t -= 10; a -= 1; }
  return t;
}
function hi(c: Card) { return c.r === 1 ? 14 : c.r; }

export function FeltGame({ onExit, onResult }: GameProps) {
  const [chips, setChips] = useState(() => loadJson('felt:chips', 1000));
  const [peak, setPeak] = useState(() => loadJson('felt:peak', 1000));
  const [table, setTable] = useState<Table>('lobby');
  const [msg, setMsg] = useState('The room is yours.');

  useEffect(() => {
    void arcadeAudio.playMusic('felt', '/assets/music/Night-at-the-Beach.ogg');
    return () => arcadeAudio.stopMusic();
  }, []);

  const bank = (n: number) => {
    setChips((c) => {
      const next = Math.max(0, c + n);
      saveJson('felt:chips', next);
      setPeak((p) => {
        const pk = Math.max(p, next);
        saveJson('felt:peak', pk);
        return pk;
      });
      return next;
    });
  };

  const cashout = () => {
    onResult({
      score: peak,
      won: peak >= 2500,
      stamps: { clear: peak >= 2500, skill: peak >= 5000, mastery: peak >= 10000 },
      note: `Peak bankroll ${peak}`,
    });
    onExit();
  };

  return (
    <div className="felt">
      <header className="felt-bar">
        <strong>The Felt</strong>
        <span>{chips.toLocaleString()} chips · peak {peak.toLocaleString()}</span>
        <div>
          {table !== 'lobby' && <button type="button" className="ghost" onClick={() => setTable('lobby')}>Tables</button>}
          <button type="button" className="ghost" onClick={cashout}>Cash out</button>
        </div>
      </header>
      <p className="felt-msg">{msg}</p>
      {table === 'lobby' && (
        <div className="felt-lobby">
          {[
            ['blackjack', 'Blackjack', 'Hit 21. Double. Split pairs.'],
            ['holdem', 'Hold\'em', 'Three named AIs. One winner.'],
            ['klondike', 'Klondike', 'Build the four houses.'],
            ['pyramid', 'Pyramid', 'Pair to 13. Clear the stones.'],
            ['highlow', 'High-Low', 'Call the next card. Ride a streak.'],
          ].map(([id, t, d]) => (
            <button key={id} type="button" className="table-card" onClick={() => setTable(id as Table)}>
              <h3>{t}</h3>
              <p>{d}</p>
            </button>
          ))}
        </div>
      )}
      {table === 'blackjack' && <Blackjack chips={chips} bank={bank} say={setMsg} />}
      {table === 'holdem' && <Holdem chips={chips} bank={bank} say={setMsg} />}
      {table === 'klondike' && <Klondike say={setMsg} onWin={() => bank(250)} />}
      {table === 'pyramid' && <Pyramid say={setMsg} onWin={() => bank(200)} />}
      {table === 'highlow' && <HighLow chips={chips} bank={bank} say={setMsg} />}
    </div>
  );
}

function CardView({ c, back, onClick, small }: { c?: Card; back?: boolean; onClick?: () => void; small?: boolean }) {
  return (
    <button type="button" className={`pcard ${small ? 'sm' : ''}`} onClick={onClick} disabled={!onClick}>
      <img src={back || !c ? '/assets/cards/BACK.png' : face(c)} alt="" draggable={false} />
    </button>
  );
}

function Blackjack({ chips, bank, say }: { chips: number; bank: (n: number) => void; say: (s: string) => void }) {
  const [bet, setBet] = useState(50);
  const [d, setD] = useState<Card[]>([]);
  const [ph, setPh] = useState<Card[]>([]);
  const [dh, setDh] = useState<Card[]>([]);
  const [hide, setHide] = useState(true);
  const [live, setLive] = useState(false);

  const deal = () => {
    if (chips < bet) return say('Not enough chips.');
    bank(-bet);
    const nd = deck();
    const p = [nd.pop()!, nd.pop()!];
    const h = [nd.pop()!, nd.pop()!];
    setD(nd); setPh(p); setDh(h); setHide(true); setLive(true);
    arcadeAudio.blip(400, 0.05);
    if (valBJ(p) === 21) {
      setHide(false); setLive(false);
      bank(Math.floor(bet * 2.5));
      say('Blackjack.');
    } else say('Hit, stand, or double.');
  };
  const hit = () => {
    if (!live) return;
    const nd = [...d];
    const p = [...ph, nd.pop()!];
    setD(nd); setPh(p);
    if (valBJ(p) > 21) { setLive(false); setHide(false); say('Bust.'); }
  };
  const resolve = (hand: Card[], stake: number) => {
    const nd = [...d];
    const house = [...dh];
    while (valBJ(house) < 17) house.push(nd.pop()!);
    setDh(house); setD(nd); setHide(false); setLive(false);
    const pv = valBJ(hand); const hv = valBJ(house);
    if (pv > 21) say('Bust.');
    else if (hv > 21 || pv > hv) { bank(stake * 2); say('You take the pot.'); }
    else if (pv === hv) { bank(stake); say('Push.'); }
    else say('House.');
  };
  const stand = () => resolve(ph, bet);
  const dbl = () => {
    if (!live || chips < bet) return;
    bank(-bet);
    const nd = [...d];
    const p = [...ph, nd.pop()!];
    setD(nd); setPh(p);
    resolve(p, bet * 2);
  };

  return (
    <div className="felt-table">
      <div className="hands">
        <div>
          <p>Dealer · {hide ? '?' : valBJ(dh)}</p>
          <div className="row">{dh.map((c, i) => <CardView key={c.id} c={c} back={hide && i === 1} />)}</div>
        </div>
        <div>
          <p>You · {ph.length ? valBJ(ph) : '—'}</p>
          <div className="row">{ph.map((c) => <CardView key={c.id} c={c} />)}</div>
        </div>
      </div>
      <div className="felt-actions">
        {!live && (
          <>
            <label>Bet <input type="range" min={25} max={Math.max(25, chips)} step={25} value={Math.min(bet, chips || 25)} onChange={(e) => setBet(+e.target.value)} /></label>
            <button type="button" className="play" onClick={deal}>Deal {bet}</button>
          </>
        )}
        {live && (
          <>
            <button type="button" className="play" onClick={hit}>Hit</button>
            <button type="button" className="play" onClick={stand}>Stand</button>
            <button type="button" className="ghost" onClick={dbl}>Double</button>
          </>
        )}
      </div>
    </div>
  );
}

function rankHoldem(cards: Card[]): [number, string] {
  const ranks = cards.map((c) => (c.r === 1 ? 14 : c.r)).sort((a, b) => b - a);
  const suits = new Map<Suit, number>();
  const rc = new Map<number, number>();
  for (const c of cards) {
    suits.set(c.s, (suits.get(c.s) || 0) + 1);
    const r = c.r === 1 ? 14 : c.r;
    rc.set(r, (rc.get(r) || 0) + 1);
  }
  const flush = [...suits.values()].some((n) => n >= 5);
  const uniq = [...new Set(ranks)].sort((a, b) => b - a);
  let straight = 0;
  const seq = uniq.includes(14) ? [...uniq, 1] : uniq;
  for (let i = 0; i < seq.length; i++) {
    let run = 1;
    for (let j = i; j < seq.length - 1 && seq[j] - 1 === seq[j + 1]; j++) run++;
    if (run >= 5) straight = Math.max(straight, seq[i]);
  }
  const counts = [...rc.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  if (flush && straight) return [8, 'Straight flush'];
  if (counts[0][1] === 4) return [7, 'Four of a kind'];
  if (counts[0][1] === 3 && counts[1]?.[1] >= 2) return [6, 'Full house'];
  if (flush) return [5, 'Flush'];
  if (straight) return [4, 'Straight'];
  if (counts[0][1] === 3) return [3, 'Trips'];
  if (counts[0][1] === 2 && counts[1]?.[1] === 2) return [2, 'Two pair'];
  if (counts[0][1] === 2) return [1, 'Pair'];
  return [0, 'High card'];
}

function Holdem({ chips, bank, say }: { chips: number; bank: (n: number) => void; say: (s: string) => void }) {
  const names = useMemo(() => ['Iris', 'Cole', 'Moth'], []);
  const [bet] = useState(50);
  const [board, setBoard] = useState<Card[]>([]);
  const [you, setYou] = useState<Card[]>([]);
  const [ai, setAi] = useState<Card[][]>([]);
  const [folded, setFolded] = useState([false, false, false]);
  const [street, setStreet] = useState(0);
  const [pot, setPot] = useState(0);
  const [live, setLive] = useState(false);

  const deal = () => {
    if (chips < bet) return say('Need chips.');
    bank(-bet);
    const nd = deck();
    const y = [nd.pop()!, nd.pop()!];
    const a = [0, 1, 2].map(() => [nd.pop()!, nd.pop()!]);
    setYou(y); setAi(a); setBoard([]); setStreet(0); setPot(bet * 4); setFolded([false, false, false]); setLive(true);
    say('Hole cards. Call the flop or fold.');
  };
  const next = (fold = false) => {
    if (!live) return;
    if (fold) { setLive(false); say('Fold.'); return; }
    const nd = deck().filter((c) => ![...you, ...ai.flat(), ...board].some((x) => x.r === c.r && x.s === c.s));
    let b = [...board];
    if (street === 0) b = [nd.pop()!, nd.pop()!, nd.pop()!];
    else b = [...b, nd.pop()!];
    const nf = folded.map((f, i) => {
      if (f) return true;
      const [sc] = rankHoldem([...ai[i], ...b]);
      return sc === 0 && Math.random() < 0.25;
    });
    setFolded(nf);
    setBoard(b);
    setStreet((s) => s + 1);
    if (street >= 2) {
      const yours = rankHoldem([...you, ...b]);
      let win = true;
      let best = yours[0];
      nf.forEach((f, i) => {
        if (f) return;
        const sc = rankHoldem([...ai[i], ...b])[0];
        if (sc > best) { win = false; best = sc; }
        if (sc === yours[0] && sc === best) win = false;
      });
      setLive(false);
      if (win) { bank(pot); say(`${yours[1]}. You take ${pot}.`); }
      else say(`${yours[1]}. The table takes it.`);
    } else say(street === 0 ? 'Flop. Call the turn?' : 'Turn. Call the river?');
  };

  return (
    <div className="felt-table">
      <div className="row">{board.map((c) => <CardView key={c.id} c={c} />)}</div>
      <p>Pot {pot} · You</p>
      <div className="row">{you.map((c) => <CardView key={c.id} c={c} />)}</div>
      <div className="ai-row">
        {names.map((n, i) => (
          <div key={n} className={folded[i] ? 'dim' : ''}>
            <p>{n}{folded[i] ? ' folded' : ''}</p>
            <div className="row">{(ai[i] || []).map((c) => <CardView key={c.id} c={c} back={live || street < 3} small />)}</div>
          </div>
        ))}
      </div>
      <div className="felt-actions">
        {!live && <button type="button" className="play" onClick={deal}>Deal {bet}</button>}
        {live && (
          <>
            <button type="button" className="play" onClick={() => next(false)}>Call</button>
            <button type="button" className="ghost" onClick={() => next(true)}>Fold</button>
          </>
        )}
      </div>
    </div>
  );
}

function Klondike({ say, onWin }: { say: (s: string) => void; onWin: () => void }) {
  const make = () => {
    const d = deck();
    const tab: Card[][] = [];
    for (let i = 0; i < 7; i++) tab.push(d.splice(0, i + 1));
    return {
      tab,
      stock: d,
      waste: [] as Card[],
      found: [[], [], [], []] as Card[][],
      open: new Set(tab.map((col, i) => col[i]?.id).filter((x): x is string => !!x)),
    };
  };
  const [st, setSt] = useState(make);

  const draw = () => {
    setSt((s) => {
      if (!s.stock.length) return { ...s, stock: [...s.waste].reverse(), waste: [] };
      const stock = [...s.stock];
      const waste = [...s.waste, stock.pop()!];
      return { ...s, stock, waste };
    });
  };
  const toFound = (c: Card, from: 'waste' | number) => {
    setSt((s) => {
      const found = s.found.map((f) => [...f]);
      const i = SUITS.indexOf(c.s);
      const top = found[i][found[i].length - 1];
      const ok = (!top && c.r === 1) || (top && c.r === top.r + 1);
      if (!ok) return s;
      found[i].push(c);
      const next = { ...s, found, waste: [...s.waste], tab: s.tab.map((t) => [...t]) };
      if (from === 'waste') next.waste.pop();
      else {
        next.tab[from].pop();
        const last = next.tab[from][next.tab[from].length - 1];
        if (last) {
          const open = new Set(s.open);
          open.add(last.id);
          next.open = open;
        }
      }
      if (found.every((f) => f.length === 13)) {
        say('Houses complete.');
        onWin();
      }
      return next;
    });
  };

  return (
    <div className="felt-table klondike">
      <div className="row">
        <CardView back onClick={draw} />
        {st.waste.length ? <CardView c={st.waste[st.waste.length - 1]} onClick={() => toFound(st.waste[st.waste.length - 1], 'waste')} /> : <div className="ph" />}
        {st.found.map((f, i) => <CardView key={i} c={f[f.length - 1]} />)}
      </div>
      <div className="k-tab">
        {st.tab.map((col, i) => (
          <div key={i} className="col">
            {col.map((c, j) => (
              <CardView
                key={c.id}
                c={c}
                back={!st.open.has(c.id)}
                small
                onClick={j === col.length - 1 && st.open.has(c.id) ? () => toFound(c, i) : undefined}
              />
            ))}
          </div>
        ))}
      </div>
      <button type="button" className="ghost" onClick={() => { setSt(make()); say('New tableau.'); }}>New deal</button>
    </div>
  );
}

function Pyramid({ say, onWin }: { say: (s: string) => void; onWin: () => void }) {
  const build = () => {
    const d = deck();
    const rows: (Card | null)[][] = [];
    for (let r = 1; r <= 7; r++) rows.push(d.splice(0, r));
    return { rows, stock: d, waste: [] as Card[] };
  };
  const [st, setSt] = useState(build);
  const [sel, setSel] = useState<Card | null>(null);
  const free = (r: number, c: number) => {
    if (r === 6) return true;
    return !st.rows[r + 1][c] && !st.rows[r + 1][c + 1];
  };
  const v = (c: Card) => Math.min(13, c.r === 1 ? 1 : c.r);
  const tryPair = (a: Card, b: Card, clearA: () => void, clearB: () => void) => {
    if (v(a) + v(b) === 13 || (a === b && v(a) === 13)) {
      clearA(); clearB();
      arcadeAudio.blip(500, 0.06);
      setSel(null);
    } else setSel(b);
  };
  useEffect(() => {
    if (st.rows.every((row) => row.every((c) => !c))) {
      say('Pyramid cleared.');
      onWin();
    }
  }, [st, onWin, say]);

  return (
    <div className="felt-table">
      <div className="pyr">
        {st.rows.map((row, r) => (
          <div key={r} className="row">
            {row.map((c, i) => c && (
              <CardView
                key={c.id}
                c={c}
                small
                onClick={free(r, i) ? () => {
                  if (v(c) === 13) {
                    setSt((s) => { const rows = s.rows.map((rr) => [...rr]); rows[r][i] = null; return { ...s, rows }; });
                    return;
                  }
                  if (!sel) { setSel(c); return; }
                  tryPair(sel, c, () => {
                    setSt((s) => {
                      const rows = s.rows.map((rr) => [...rr]);
                      for (const row of rows) {
                        const ix = row.findIndex((x) => x?.id === sel.id);
                        if (ix >= 0) row[ix] = null;
                      }
                      return { ...s, rows };
                    });
                  }, () => {
                    setSt((s) => { const rows = s.rows.map((rr) => [...rr]); rows[r][i] = null; return { ...s, rows }; });
                  });
                } : undefined}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="row">
        <CardView back onClick={() => setSt((s) => {
          if (!s.stock.length) return s;
          const stock = [...s.stock];
          return { ...s, stock, waste: [...s.waste, stock.pop()!] };
        })} />
        {st.waste[st.waste.length - 1] && (
          <CardView c={st.waste[st.waste.length - 1]} onClick={() => setSel(st.waste[st.waste.length - 1])} />
        )}
      </div>
    </div>
  );
}

function HighLow({ chips, bank, say }: { chips: number; bank: (n: number) => void; say: (s: string) => void }) {
  const [bet, setBet] = useState(25);
  const [cur, setCur] = useState<Card>(() => deck()[0]);
  const [streak, setStreak] = useState(0);
  const call = (dir: 'h' | 'l') => {
    if (chips < bet) return say('Need chips.');
    bank(-bet);
    const n = deck()[0];
    const ok = dir === 'h' ? hi(n) >= hi(cur) : hi(n) <= hi(cur);
    setCur(n);
    if (ok) {
      const pay = bet * (streak >= 4 ? 3 : streak >= 2 ? 2 : 1.8);
      bank(Math.floor(pay));
      setStreak((s) => s + 1);
      say(`Yes. Streak ${streak + 1}.`);
    } else {
      setStreak(0);
      say('Wrong call.');
    }
  };
  return (
    <div className="felt-table">
      <CardView c={cur} />
      <p>Streak {streak}</p>
      <label>Bet <input type="range" min={10} max={Math.max(10, chips)} step={10} value={Math.min(bet, chips || 10)} onChange={(e) => setBet(+e.target.value)} /></label>
      <div className="felt-actions">
        <button type="button" className="play" onClick={() => call('h')}>Higher</button>
        <button type="button" className="play" onClick={() => call('l')}>Lower</button>
      </div>
    </div>
  );
}
